import { useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { Camera, Upload, Loader2, CheckCircle, X, ImagePlus } from "lucide-react";

const PHOTO_TYPES = [
  { value: "wound", label: "Wound / Skin Issue" },
  { value: "rash", label: "Rash / Irritation" },
  { value: "swelling", label: "Swelling" },
  { value: "bruise", label: "Bruise / Discoloration" },
  { value: "medication", label: "Medication Question" },
  { value: "other", label: "Other" },
];

const BODY_AREAS = [
  { value: "head", label: "Head / Face" },
  { value: "neck", label: "Neck" },
  { value: "chest", label: "Chest" },
  { value: "back", label: "Back" },
  { value: "abdomen", label: "Abdomen" },
  { value: "arm_left", label: "Left Arm" },
  { value: "arm_right", label: "Right Arm" },
  { value: "hand_left", label: "Left Hand" },
  { value: "hand_right", label: "Right Hand" },
  { value: "leg_left", label: "Left Leg" },
  { value: "leg_right", label: "Right Leg" },
  { value: "foot_left", label: "Left Foot" },
  { value: "foot_right", label: "Right Foot" },
  { value: "other", label: "Other" },
];

export default function SymptomPhotoUpload({ patientId, patientName, onUploadComplete }) {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [showDialog, setShowDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [photoType, setPhotoType] = useState("");
  const [bodyArea, setBodyArea] = useState("");
  const [description, setDescription] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/png", "image/webp", "image/heic"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please select a valid image file (JPEG, PNG, WebP, or HEIC)");
        return;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File is too large. Maximum size is 10MB.");
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
      
      setShowDialog(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !photoType) {
      toast.error("Please select a photo type");
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      
      const params = new URLSearchParams({
        patient_id: patientId || user?.id,
        photo_type: photoType,
      });
      
      if (bodyArea) params.append("body_area", bodyArea);
      if (description) params.append("description", description);
      
      const response = await api.post(`/symptom-photos/upload?${params.toString()}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.success) {
        setUploadSuccess(true);
        toast.success("Photo uploaded! Your care team will review it shortly.");
        
        setTimeout(() => {
          resetForm();
          if (onUploadComplete) onUploadComplete();
        }, 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setShowDialog(false);
    setSelectedFile(null);
    setPreview(null);
    setPhotoType("");
    setBodyArea("");
    setDescription("");
    setUploadSuccess(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <>
      {/* Upload Button */}
      <Button 
        variant="outline" 
        className="h-auto py-3 flex-col gap-1 w-full"
        onClick={() => fileInputRef.current?.click()}
        data-testid="symptom-photo-upload-btn"
      >
        <Camera className="w-5 h-5" />
        <span className="text-xs">Photo Upload</span>
      </Button>
      
      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Upload Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => !uploading && (open ? null : resetForm())}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              Upload Symptom Photo
            </DialogTitle>
            <DialogDescription>
              Share a photo with your care team for review. They will respond as soon as possible.
            </DialogDescription>
          </DialogHeader>
          
          {uploadSuccess ? (
            <div className="py-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <p className="text-lg font-medium">Photo Uploaded Successfully!</p>
              <p className="text-sm text-muted-foreground mt-1">
                Your care team will review it and reach out if needed.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Preview */}
              {preview && (
                <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                  <img 
                    src={preview} 
                    alt="Preview" 
                    className="w-full h-full object-contain"
                  />
                  <Button 
                    variant="destructive" 
                    size="icon" 
                    className="absolute top-2 right-2"
                    onClick={() => {
                      setSelectedFile(null);
                      setPreview(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Photo Type */}
              <div>
                <Label>What type of symptom is this? *</Label>
                <Select value={photoType} onValueChange={setPhotoType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PHOTO_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Body Area */}
              <div>
                <Label>Body area (optional)</Label>
                <Select value={bodyArea} onValueChange={setBodyArea}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select body area..." />
                  </SelectTrigger>
                  <SelectContent>
                    {BODY_AREAS.map((area) => (
                      <SelectItem key={area.value} value={area.value}>
                        {area.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe any pain, changes, or concerns..."
                  rows={3}
                />
              </div>

              {/* Privacy Note */}
              <p className="text-xs text-muted-foreground">
                This photo will only be visible to your assigned care team for clinical review.
              </p>
            </div>
          )}

          {!uploadSuccess && (
            <DialogFooter>
              <Button variant="outline" onClick={resetForm} disabled={uploading}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={uploading || !photoType}>
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Photo
                  </>
                )}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
